import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  CardActions,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  DirectionsBoat as MarinaIcon,
  Storefront as StoreIcon,
  Restaurant as KitchenIcon,
  Business as BusinessIcon,
  People as StaffIcon,
  BarChart as ReportsIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, getDocs, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { getStaffMembers } from '../../firebase/services/userService';

// Interface for dashboard data
interface DashboardData {
  customers: number;
  activeStaff: number;
  vessels: number;
  weeklyReservations: number;
  monthlyReservations: number;
  seasonalReservations: number;
  weeklyRevenue: number;
  marinaRevenue: number;
  storeRevenue: number;
  kitchenRevenue: number;
  alerts: Array<{
    title: string;
    date: string;
    type: 'primary' | 'warning' | 'error' | 'info' | 'success';
  }>;
}

const BusinessDashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    customers: 0,
    activeStaff: 0,
    vessels: 0,
    weeklyReservations: 0,
    monthlyReservations: 0,
    seasonalReservations: 0,
    weeklyRevenue: 0,
    marinaRevenue: 0,
    storeRevenue: 0,
    kitchenRevenue: 0,
    alerts: []
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get current date for time-based queries
        const now = new Date();
        const oneWeekAgo = startOfWeek(now);
        const monthStart = startOfMonth(now);
        const yearStart = startOfYear(now);
        
        // Initialize data object
        const data: Partial<DashboardData> = {
          customers: 0,
          activeStaff: 0,
          vessels: 0,
          weeklyReservations: 0,
          monthlyReservations: 0,
          seasonalReservations: 0,
          weeklyRevenue: 0,
          marinaRevenue: 0,
          storeRevenue: 0,
          kitchenRevenue: 0,
          alerts: []
        };
        
        // Count customers - handled separately with try/catch
        try {
          const customersSnapshot = await getDocs(collection(db, 'customers'));
          data.customers = customersSnapshot.size;
        } catch (err) {
          console.log('Error fetching customers data:', err);
        }
        
        // Count staff members using the same approach as the staff management page
        try {
          const staffMembers = await getStaffMembers();
          data.activeStaff = staffMembers.length;
        } catch (err) {
          console.log('Error fetching staff data:', err);
        }
        
        // Count vessels - handled separately with try/catch
        try {
          const vesselsSnapshot = await getDocs(collection(db, 'vessels'));
          data.vessels = vesselsSnapshot.size;
        } catch (err) {
          console.log('Error fetching vessels data:', err);
        }
        
        // Get weekly reservations - handled separately with try/catch
        try {
          const weeklyReservationsQuery = query(
            collection(db, 'reservations'),
            where('date', '>=', format(oneWeekAgo, 'yyyy-MM-dd')),
            where('date', '<=', format(endOfWeek(now), 'yyyy-MM-dd'))
          );
          const weeklyReservationsSnapshot = await getDocs(weeklyReservationsQuery);
          data.weeklyReservations = weeklyReservationsSnapshot.size;
        } catch (err) {
          console.log('Error fetching weekly reservations:', err);
        }
        
        // Get monthly reservations - handled separately with try/catch
        try {
          const monthlyReservationsQuery = query(
            collection(db, 'reservations'),
            where('date', '>=', format(monthStart, 'yyyy-MM-dd')),
            where('date', '<=', format(endOfMonth(now), 'yyyy-MM-dd'))
          );
          const monthlyReservationsSnapshot = await getDocs(monthlyReservationsQuery);
          data.monthlyReservations = monthlyReservationsSnapshot.size;
        } catch (err) {
          console.log('Error fetching monthly reservations:', err);
        }
        
        // Get seasonal (yearly) reservations - handled separately with try/catch
        try {
          const seasonalReservationsQuery = query(
            collection(db, 'reservations'),
            where('date', '>=', format(yearStart, 'yyyy-MM-dd')),
            where('date', '<=', format(endOfYear(now), 'yyyy-MM-dd'))
          );
          const seasonalReservationsSnapshot = await getDocs(seasonalReservationsQuery);
          data.seasonalReservations = seasonalReservationsSnapshot.size;
        } catch (err) {
          console.log('Error fetching seasonal reservations:', err);
        }
        
        // Set all revenue fields to $0 as requested
        data.weeklyRevenue = 0;
        data.marinaRevenue = 0;
        data.storeRevenue = 0;
        data.kitchenRevenue = 0;
        
        // Get maintenance alerts only (no sample business alerts)
        try {
          const maintenanceQuery = query(
            collection(db, 'maintenance'),
            where('status', '==', 'pending'),
            orderBy('dueDate', 'asc'),
            limit(5)
          );
          const maintenanceSnapshot = await getDocs(maintenanceQuery);
          const maintenanceAlerts = maintenanceSnapshot.docs.map(doc => {
            const docData = doc.data();
            return {
              title: docData.title || 'Maintenance Required',
              date: docData.dueDate ? format(docData.dueDate.toDate(), 'MMMM d, yyyy') : 'No due date',
              type: 'warning' as const
            };
          });
          
          data.alerts = maintenanceAlerts;
        } catch (err) {
          console.log('Error fetching maintenance data:', err);
          data.alerts = [];
        }
        
        // Update dashboard data
        setDashboardData(data as DashboardData);
        
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load some dashboard data. Some information may be incomplete.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get safe values with fallbacks for missing data
  const getSafeValue = (value: number | undefined): number => {
    return typeof value === 'number' ? value : 0;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Fallen Leaf Marina Business Administration
      </Typography>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Welcome, {userProfile?.firstName || 'Admin'}
      </Typography>
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* Business overview */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Business Overview
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Card 
                  onClick={() => navigate('/admin/business/customers')}
                  sx={{ 
                    textAlign: 'center', 
                    bgcolor: 'primary.light', 
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      bgcolor: 'primary.main'
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="h3">{getSafeValue(dashboardData.customers)}</Typography>
                    <Typography variant="subtitle1">Total Customers</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card 
                  onClick={() => navigate('/admin/business/staff')}
                  sx={{ 
                    textAlign: 'center', 
                    bgcolor: 'secondary.light', 
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      bgcolor: 'secondary.main'
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="h3">{getSafeValue(dashboardData.activeStaff)}</Typography>
                    <Typography variant="subtitle1">Active Staff Members</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card 
                  onClick={() => navigate('/admin/business/reports/overview')}
                  sx={{ 
                    textAlign: 'center', 
                    bgcolor: 'success.light', 
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      bgcolor: 'success.main'
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="h3">{formatCurrency(getSafeValue(dashboardData.weeklyRevenue))}</Typography>
                    <Typography variant="subtitle1">Weekly Revenue</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Reservation Statistics
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Paper 
                  onClick={() => navigate('/staff/reservations')}
                  sx={{ 
                    p: 2, 
                    bgcolor: 'background.default',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                      bgcolor: 'grey.50'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MarinaIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">This Week</Typography>
                  </Box>
                  <Typography variant="h4" color="primary" sx={{ mb: 1 }}>
                    {getSafeValue(dashboardData.weeklyReservations)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Reservations</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper 
                  onClick={() => navigate('/staff/reservations')}
                  sx={{ 
                    p: 2, 
                    bgcolor: 'background.default',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                      bgcolor: 'grey.50'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MarinaIcon color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">This Month</Typography>
                  </Box>
                  <Typography variant="h4" color="secondary" sx={{ mb: 1 }}>
                    {getSafeValue(dashboardData.monthlyReservations)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Reservations</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper 
                  onClick={() => navigate('/staff/reservations')}
                  sx={{ 
                    p: 2, 
                    bgcolor: 'background.default',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                      bgcolor: 'grey.50'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MarinaIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">This Season</Typography>
                  </Box>
                  <Typography variant="h4" color="success.main" sx={{ mb: 1 }}>
                    {getSafeValue(dashboardData.seasonalReservations)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Reservations</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Department Performance
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MarinaIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">Marina</Typography>
                  </Box>
                  <Typography variant="body2">Revenue: {formatCurrency(getSafeValue(dashboardData.marinaRevenue))}</Typography>
                  <Typography variant="body2">Reservations: {getSafeValue(dashboardData.weeklyReservations)}</Typography>
                  <Typography variant="body2">Vessels: {getSafeValue(dashboardData.vessels)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <StoreIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">Store</Typography>
                  </Box>
                  <Typography variant="body2">Revenue: {formatCurrency(getSafeValue(dashboardData.storeRevenue))}</Typography>
                  <Typography variant="body2">Sales: 0</Typography>
                  <Typography variant="body2">Utilization: 0%</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <KitchenIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">Kitchen</Typography>
                  </Box>
                  <Typography variant="body2">Revenue: {formatCurrency(getSafeValue(dashboardData.kitchenRevenue))}</Typography>
                  <Typography variant="body2">Orders: 0</Typography>
                  <Typography variant="body2">Utilization: 0%</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Important alerts */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Maintenance Alerts
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              {dashboardData.alerts && dashboardData.alerts.length > 0 ? (
                dashboardData.alerts.map((alert, index) => (
                  <ListItem key={index} divider={index < dashboardData.alerts.length - 1}>
                    <ListItemText 
                      primary={alert.title} 
                      secondary={alert.date} 
                      primaryTypographyProps={{ color: `${alert.type}.main` }}
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText 
                    primary="No Maintenance Alerts" 
                    secondary="All systems are running smoothly!" 
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
        
        {/* Department cards */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Department Management
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MarinaIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h5">Marina</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Manage vessels, reservations, and marina operations. View dock status and upcoming bookings.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button component={Link} to="/admin/dashboard" size="small" color="primary">
                    Go to Marina Admin
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <StoreIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h5">Store</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Manage store inventory, products, sales, and promotions. Track stock levels and top-selling items.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button component={Link} to="/admin/store/dashboard" size="small" color="primary">
                    Go to Store Admin
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <KitchenIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h5">Kitchen</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Manage menu items, food inventory, and kitchen orders. Track popular dishes and ingredient levels.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button component={Link} to="/admin/kitchen/dashboard" size="small" color="primary">
                    Go to Kitchen Admin
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        
        {/* Business management tools */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Business Management
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Card component={Link} to="/admin/business/locations" sx={{ textAlign: 'center', textDecoration: 'none' }}>
                <CardContent>
                  <BusinessIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="subtitle1" color="primary">
                    Company Info
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card component={Link} to="/admin/staff" sx={{ textAlign: 'center', textDecoration: 'none' }}>
                <CardContent>
                  <StaffIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="subtitle1" color="primary">
                    Staff Directory
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card component={Link} to="/admin/business/reports/overview" sx={{ textAlign: 'center', textDecoration: 'none' }}>
                <CardContent>
                  <ReportsIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="subtitle1" color="primary">
                    Business Reports
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card component={Link} to="/admin/business/settings/general" sx={{ textAlign: 'center', textDecoration: 'none' }}>
                <CardContent>
                  <SettingsIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="subtitle1" color="primary">
                    Global Settings
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BusinessDashboard; 